import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Grid,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { QuestionAnswer as QuestionIcon } from "@mui/icons-material";
import _ from "underscore";
import { isQuestionAnswered } from "../../../../../utils/optionalDataUtils";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const QuestionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
}));

const WeeklyQuestionsCard = ({ report }) => {
  if (!report) return null;

  // Check if we have a note or any optional fields with values
  const hasNote =
    report.extraData &&
    report.extraData.note &&
    report.extraData.note.length > 0;

  // Process optional questions if they exist
  let optionals = [];
  if (report.optionals && report.optionals.length > 0) {
    optionals = report.optionals.filter((o) => isQuestionAnswered(o));
  } else if (report.extraData && report.extraData.optionals) {
    // Alternative format where optionals are in extraData.optionals
    const keys = _.keys(report.extraData.optionals);
    optionals = keys
      .map((key) => report.extraData.optionals[key])
      .filter((o) => isQuestionAnswered(o));
  }

  // If weekly optional answers exist, process them
  let weeklyOptionals = [];
  if (report.weeklyOptionals && report.weeklyOptionals.length > 0) {
    weeklyOptionals = report.weeklyOptionals.filter((o) =>
      isQuestionAnswered(o),
    );
  }

  // If there's no content to show, don't render the card
  if (!hasNote && optionals.length === 0 && weeklyOptionals.length === 0) {
    return null;
  }

  return (
    <StyledCard>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <QuestionIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Weekly Questions & Notes
            </Typography>
          </Stack>
        }
      />
      <CardContentWrapper>
        {/* Client's note to coach */}
        {hasNote && (
          <QuestionPaper>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Note to Coach
            </Typography>
            {/* Changed to 'span' to avoid p inside p */}
            <Typography component="span" variant="body1">
              {report.extraData.note}
            </Typography>
          </QuestionPaper>
        )}

        {/* Weekly optionals */}
        {weeklyOptionals.length > 0 && (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mt: 2, mb: 1 }}
          >
            Weekly Questions
          </Typography>
        )}

        <Grid container spacing={2}>
          {weeklyOptionals.map((optional, index) => (
            <Grid item xs={12} md={6} key={index}>
              <QuestionPaper>
                <Typography variant="subtitle2" fontWeight="bold">
                  {optional.name}
                </Typography>
                {optional.subTitle && (
                  <Typography
                    component="span" // Changed to 'span'
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    {optional.subTitle}
                  </Typography>
                )}
                <Typography component="span" variant="body1">
                  {" "}
                  {/* Changed to 'span' */}
                  {typeof optional.value === "number"
                    ? Math.round(optional.value)
                    : optional.value}
                </Typography>
              </QuestionPaper>
            </Grid>
          ))}
        </Grid>

        {/* Daily optionals */}
        {optionals.length > 0 && (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mt: 2, mb: 1 }}
          >
            Daily Questions
          </Typography>
        )}

        <Grid container spacing={2}>
          {optionals.map((optional, index) => (
            <Grid item xs={12} md={6} key={index}>
              <QuestionPaper>
                <Typography variant="subtitle2" fontWeight="bold">
                  {optional.name}
                </Typography>
                {optional.subTitle && (
                  <Typography
                    component="span" // Changed to 'span'
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    {optional.subTitle}
                  </Typography>
                )}
                <Typography component="span" variant="body1">
                  {" "}
                  {/* Changed to 'span' */}
                  {typeof optional.value === "number"
                    ? Math.round(optional.value)
                    : optional.value}
                </Typography>
              </QuestionPaper>
            </Grid>
          ))}
        </Grid>

        {/* Show a message if there are no entries despite the card being shown */}
        {!hasNote && optionals.length === 0 && weeklyOptionals.length === 0 && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No questions answered or notes for this reporting period
            </Typography>
          </Box>
        )}
      </CardContentWrapper>
    </StyledCard>
  );
};

export default WeeklyQuestionsCard;
